import { createClient } from "@supabase/supabase-js";
import { analytics } from "./analytics";
import { trackLead } from "./metaPixel";

import { getSubscriptionInfo } from "./subscription";

export const USER_AUTHORIZATION = "USER_AUTHORIZATION";
export const AUTHORIZED_KEY = "true";
export const NOT_AUTHORIZED_KEY = "false";
export const HIDE_ONBOARDING = "HIDE_ONBOARDING";

const defaultSupabaseUrl = "https://iusmnwdnqzmeizyjqkvw.supabase.co";
const defaultSupabasePublishableKey =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Iml1c21ud2RucXptZWl6eWpxa3Z3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDQyNzcyNjUsImV4cCI6MjA1OTg1MzI2NX0.2cVofMURvnCY38YNvpRZlKoDyJ-nVcJeS-qgyABSGvU";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || defaultSupabaseUrl;
const supabasePublishableKey =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  defaultSupabasePublishableKey;
const defaultAuthRedirectUrl = "https://app.tools-products.com";

export const supabase = createClient(supabaseUrl, supabasePublishableKey, {
  auth: {
    autoRefreshToken: true,
    detectSessionInUrl: true,
    persistSession: true,
  },
});

type SetUser = (user: any) => void;

const redirectToCurrentPage = () => {
  const configuredRedirectUrl = process.env.REACT_APP_AUTH_REDIRECT_URL;

  if (configuredRedirectUrl) {
    return configuredRedirectUrl;
  }

  const isLocalhost = ["localhost", "127.0.0.1"].includes(
    window.location.hostname
  );

  if (isLocalhost) {
    return window.location.href.split("#")[0];
  }

  return defaultAuthRedirectUrl;
};

const providerForUser = (user: any) =>
  user.app_metadata?.provider || "email";

const hydrateUser = async (authUser: any, setUser: SetUser) => {
  if (!authUser.email) {
    localStorage.setItem(USER_AUTHORIZATION, NOT_AUTHORIZED_KEY);
    setUser(null);
    return null;
  }

  const {
    hasPremium,
    subscriptionEnd,
    isNewSubscriber,
    isCanceled,
    plan,
    subscriptionAfterTrail,
    subscriptionSource,
  } = await getSubscriptionInfo(authUser.email, authUser.id);

  const result = {
    email: authUser.email,
    id: authUser.id,
    isNewSubscriber,
    hasPremium,
    subscriptionEnd,
    isCanceled,
    plan,
    subscriptionAfterTrail,
    subscriptionSource,
  };

  analytics.identify(authUser.email, {
    user_id: authUser.id,
    auth_provider: providerForUser(authUser),
    has_premium: hasPremium,
    subscription_end: subscriptionEnd,
    subscription_is_canceled: isCanceled,
    subscription_plan_interval: plan?.interval,
    subscription_plan_amount: plan?.amount,
    subscription_is_trial: plan?.isTrial,
    subscription_after_trial: subscriptionAfterTrail,
    subscription_source: subscriptionSource,
    is_new_subscriber: isNewSubscriber,
  });
  analytics.track("User Hydrated", {
    auth_provider: providerForUser(authUser),
    has_premium: hasPremium,
    subscription_is_canceled: isCanceled,
    subscription_plan_interval: plan?.interval,
    subscription_is_trial: plan?.isTrial,
    subscription_source: subscriptionSource,
    is_new_subscriber: isNewSubscriber,
  });

  localStorage.setItem(USER_AUTHORIZATION, AUTHORIZED_KEY);
  setUser(result);
  return result;
};

export const isLoggedIn = () => {
  const isUserLoggedIn = localStorage.getItem(USER_AUTHORIZATION);
  if (isUserLoggedIn === AUTHORIZED_KEY) {
    return true;
  }
  if (isUserLoggedIn === NOT_AUTHORIZED_KEY) {
    return false;
  }
  return undefined;
};

export const checkUser = async (setUser: SetUser) => {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();

  if (error) {
    console.error(error);
    throw error;
  }

  if (!session?.user) {
    localStorage.setItem(USER_AUTHORIZATION, NOT_AUTHORIZED_KEY);
    setUser(null);
    return null;
  }

  return hydrateUser(session.user, setUser);
};

export const refreshSubscriptionStatus = async (setUser: SetUser) =>
  checkUser(setUser);

export const getRedirectResult = async (setUser: SetUser) => {
  const result = await checkUser(setUser);

  if (result?.email) {
    analytics.track("Login", {
      emailType: result.email ? "redirect" : "email",
      email: result.email,
      method: "redirect",
    });
    trackLead({ method: "redirect" });
  }

  return result;
};

export const login = async (email: string) => {
  const normalizedEmail = email.trim().toLowerCase();

  const { error } = await supabase.auth.signInWithOtp({
    email: normalizedEmail,
    options: {
      emailRedirectTo: redirectToCurrentPage(),
    },
  });

  if (error) {
    console.error(error);
    throw error;
  }

  analytics.identify(normalizedEmail, { auth_provider: "email" });
  analytics.track("Login", { email: normalizedEmail, emailType: "email", method: "email" });
  trackLead({ method: "email" });
};

export const loginWithGoogle = async () => {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo: redirectToCurrentPage(),
    },
  });

  if (error) {
    console.error(error);
    throw error;
  }

  analytics.track("OAuth Login Started", { provider: "google" });
};

export const loginWithApple = async () => {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: "apple",
    options: {
      redirectTo: redirectToCurrentPage(),
    },
  });

  if (error) {
    console.error(error);
    throw error;
  }

  analytics.track("OAuth Login Started", { provider: "apple" });
};

export const subscribeToAuthChanges = (setUser: SetUser) =>
  supabase.auth.onAuthStateChange(async (event: string, session: any) => {
    if (event === "INITIAL_SESSION" || event === "TOKEN_REFRESHED") {
      return;
    }

    if (event === "SIGNED_OUT" || !session?.user) {
      localStorage.setItem(USER_AUTHORIZATION, NOT_AUTHORIZED_KEY);
      analytics.track("Logout");
      analytics.reset();
      setUser(null);
      return;
    }

    if (event === "SIGNED_IN") {
      analytics.track("Login", {
        emailType: providerForUser(session.user),
        email: session.user.email,
        method: providerForUser(session.user),
      });
      trackLead({ method: providerForUser(session.user) });
    }

    try {
      await hydrateUser(session.user, setUser);
    } catch (error) {
      console.error(error);
    }
  }).data.subscription;

export const logout = async (setUser: SetUser) => {
  localStorage.removeItem(USER_AUTHORIZATION);

  const { error } = await supabase.auth.signOut();
  if (error) {
    console.error(error);
    throw error;
  }

  analytics.track("Logout Requested");
  analytics.reset();
  return setUser(null);
};
